/**
 * Admin OAuth2 Callback Hook
 * Processes OAuth2 callback and completes admin authentication
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  adminOAuth2Service,
  validateState,
  oauth2SessionStorage,
} from '@/lib/oauth2'
import { useAdminAuthStore } from '@/stores/adminAuthStore'
import { toast } from 'sonner'
import type { AdminAuthResponse } from '@/types/oauth'

export interface UseAdminOAuth2CallbackReturn {
  status: 'loading' | 'success' | 'error'
  error: string | null
}

/**
 * Hook for processing OAuth2 callback for admin authentication
 *
 * Automatically processes the callback on mount.
 * Handles:
 * - URL parameter validation
 * - State verification (CSRF protection)
 * - Session expiry check
 * - Token exchange
 * - Admin auth store update
 * - Navigation to dashboard
 *
 * @example
 * ```tsx
 * function OAuthCallbackPage() {
 *   const { status, error } = useAdminOAuth2Callback();
 *
 *   if (error) return <ErrorDisplay message={error} />;
 *   if (status === 'success') return <SuccessDisplay />;
 *   return <LoadingDisplay />;
 * }
 * ```
 */
export const useAdminOAuth2Callback = (): UseAdminOAuth2CallbackReturn => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hasProcessedRef = useRef(false)

  const processCallback = useCallback(async () => {
    // Prevent duplicate processing (React StrictMode, dependency changes)
    if (hasProcessedRef.current) {
      console.log('[AdminOAuth2Callback] Already processed, skipping duplicate call')
      return
    }
    hasProcessedRef.current = true

    try {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const errorParam = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      // Handle OAuth2 errors from provider
      if (errorParam) {
        const message =
          errorDescription ||
          (errorParam === 'access_denied'
            ? 'OAuth authentication was cancelled'
            : 'OAuth authentication failed')
        throw new Error(message)
      }

      // Validate required parameters
      if (!code) {
        throw new Error('Missing authorization code from OAuth provider')
      }
      if (!state) {
        throw new Error('Missing state parameter from OAuth provider')
      }

      // Retrieve stored OAuth2 data
      const session = oauth2SessionStorage.get()
      if (!session) {
        throw new Error('OAuth2 session not found. Please try logging in again.')
      }

      console.log('[AdminOAuth2Callback] Session data retrieved')

      // Validate state matches (CSRF protection)
      if (!validateState(state, session.state)) {
        throw new Error('Invalid state parameter - possible CSRF attack detected')
      }

      console.log('[AdminOAuth2Callback] State validated successfully')

      // Check session expiry
      if (oauth2SessionStorage.isExpired(session)) {
        throw new Error('OAuth2 session expired. Please try logging in again.')
      }

      console.log('[AdminOAuth2Callback] Session is valid (not expired)')

      // Exchange authorization code for tokens
      console.log('[AdminOAuth2Callback] Calling backend /api/v1/admin/oauth2/callback...')
      const response: AdminAuthResponse = await adminOAuth2Service.callback({
        provider: session.provider,
        code,
        state,
        codeVerifier: session.codeVerifier,
        redirectUri: session.redirectUri,
      })

      console.log('[AdminOAuth2Callback] Token exchange successful')
      console.log('[AdminOAuth2Callback] Admin User:', response.user.email)

      // Update admin auth store
      useAdminAuthStore.setState({
        user: {
          id: response.user.id,
          email: response.user.email,
          fullName: response.user.fullName,
          roles: response.user.roles as ('SuperAdmin' | 'BillingAdmin' | 'SupportAdmin')[],
          permissions: response.user.permissions,
        },
        token: response.token,
        refreshToken: response.refreshToken,
        isAuthenticated: true,
      })

      setStatus('success')

      // Clean up OAuth2 storage
      oauth2SessionStorage.clear()
      console.log('[AdminOAuth2Callback] Session data cleaned up')

      toast.success('Successfully signed in with TPOS Account!')

      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 1500)
    } catch (err) {
      console.error('[AdminOAuth2Callback] Processing failed:', err)

      let errorMessage = 'OAuth authentication failed. Please try again.'

      // Handle specific error messages
      if (err instanceof Error) {
        if (err.message.includes('Admin access requires an invitation')) {
          errorMessage = 'Admin access requires an invitation. Please contact your administrator to receive an invitation.'
        } else if (err.message.includes('invitation has expired')) {
          errorMessage = 'Your invitation has expired. Please request a new invitation from your administrator.'
        } else if (err.message.includes('invitation has been revoked')) {
          errorMessage = 'Your invitation has been revoked. Please contact your administrator.'
        } else {
          errorMessage = err.message
        }
      }

      setError(errorMessage)
      setStatus('error')
      toast.error(errorMessage)

      // Clean up OAuth2 storage on error
      oauth2SessionStorage.clear()

      // Redirect to login after delay
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
    }
  }, [searchParams, navigate])

  // Automatically process callback on mount
  useEffect(() => {
    processCallback()
  }, [processCallback])

  return { status, error }
}
